import type { RuleDefinition } from './contracts';
import type { MigrationPlan } from './migrationContracts';
import { ruleSummary } from './ruleHelpers';

export default function RuleExplanation({
  rule,
  recipients,
  imported = false,
}: {
  rule: RuleDefinition;
  recipients?: MigrationPlan['ruleMappings'][number]['recipients'];
  imported?: boolean;
}) {
  const channels = [
    rule.actions.notifyEmail && 'e-mail',
    rule.actions.notifyInApp && 'app og mobil',
    rule.actions.notifySms && 'SMS',
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="ua-stack">
      {(!rule.enabled || rule.mode !== 'live') && (
        <p className="ua-notice">
          {!rule.enabled
            ? 'Reglen er slået fra.'
            : rule.mode === 'test'
            ? 'Reglen er i Test.'
            : 'Reglen er sat på pause.'}{' '}
          Denne regel sender ingen driftsbeskeder.
        </p>
      )}
      <dl className="ua-rule-explanation">
        <dt>Hvad udløser alarmen?</dt>
        <dd>{ruleSummary(rule)}</dd>
        <dt>Hvornår reagerer vi?</dt>
        <dd>
          {rule.durationMinutes > 0
            ? `Når problemet har varet mindst ${rule.durationMinutes} minutter, og kontrollen bekræfter det.`
            : 'Ved næste kontrol, der bekræfter problemet. Ingen ekstra ventetid.'}
        </dd>
        <dt>Hvem får besked?</dt>
        <dd>
          {!channels ? (
            'Ingen beskedkanaler er slået til.'
          ) : recipients ? (
            <ul>
              {rule.actions.notifyEmail && (
                <li>
                  E-mail: {recipients.to.join(', ') || 'Ingen direkte modtagere'}
                  {recipients.bcc.length > 0 && <> · Skjult kopi: {recipients.bcc.join(', ')}</>}
                </li>
              )}
              {rule.actions.notifyInApp && (
                <li>
                  App og mobil: {recipients.appUserIds.length} appmodtagere ·{' '}
                  {recipients.pushUserIds.length} modtagere af mobilbeskeder
                </li>
              )}
              {rule.actions.notifySms && (
                <li>SMS: {recipients.smsPhones.join(', ') || 'Ingen konfigurerede numre'}</li>
              )}
            </ul>
          ) : (
            <>
              {channels}.{' '}
              {imported
                ? 'Modtagere fra den overførte opsætning. Se listen under enhedens Indstillinger.'
                : 'Modtagere bestemmes af enhedens ansvarlige og beskedabonnementer. Den konkrete liste vises ikke her.'}
            </>
          )}
        </dd>
        <dt>Hvor ofte?</dt>
        <dd>
          {!rule.enabled || rule.mode !== 'live' ? (
            'Ingen driftsbeskeder med reglens nuværende indstilling.'
          ) : !channels ? (
            'Ingen beskeder.'
          ) : (
            <>
              En besked ved en ny alarm.
              {rule.actions.reminderMinutes !== null
                ? ` Derefter tidligst hver ${rule.actions.reminderMinutes} minutter, mens problemet fortsætter.`
                : ' Ingen gentagne påmindelser.'}
              {rule.actions.notifyOnRecovery && rule.recovery === 'automatic'
                ? ' Også besked ved automatisk afslutning.'
                : ' Ingen besked ved normal drift.'}
            </>
          )}
        </dd>
        <dt>Hvornår slutter alarmen?</dt>
        <dd>
          {rule.recovery === 'manual'
            ? 'En person skal afslutte den. Stilhed fra enheden afslutter den ikke.'
            : `Automatisk, når data bekræfter normal drift efter reglens grænser${
                rule.recoveryMinutes > 0 ? ` i mindst ${rule.recoveryMinutes} minutter` : ''
              }.`}
        </dd>
      </dl>
      {channels && (
        <p className="ua-muted">
          Beskeder kræver aktiv samlet alarmstyring og Live på både enhed og regel. Listen viser de
          valgte modtagere og kanaler.
        </p>
      )}
      {recipients && rule.actions.notifySms && recipients.smsText && (
        <p>SMS-tekst: {recipients.smsText}</p>
      )}
      {recipients &&
        rule.actions.notifyInApp &&
        (recipients.appUserIds.length > 0 || recipients.pushUserIds.length > 0) && (
          <details>
            <summary>Vis modtagerreferencer</summary>
            <p>App: {recipients.appUserIds.join(', ') || 'Ingen'}</p>
            <p>Mobil: {recipients.pushUserIds.join(', ') || 'Ingen'}</p>
          </details>
        )}
    </div>
  );
}
